import React, { FormEvent, useRef, useEffect, useState } from 'react';
import NavigationItemMenuStructure from './navigationItemMenuStructure';
import MenuTriggerContentWithIcon from './menuTriggerContentWithIcon';
import { windowService, documentService } from '../../../utils/window.utils';
import { QueryParamsConst } from '../../../constants/query-params.constants';
import useQueryParams from '../../../hooks/useQueryParams.effect';
import Suggestions from '../../searchResults/components/suggestions';
import { WithSitecoreContextProps } from '@sitecore-jss/sitecore-jss-react/types/enhancers/withSitecoreContext';
import CTAButton, { ctaEnums } from '../../@shared/ctaButton';
import Icon from '../../@shared/icon';
import SearchInput from './searchInput';
import { IComponentFieldsGeneral } from '../../../types/types';

const SearchMenu = ({ fields, sitecoreContext }: WithSitecoreContextProps & { fields: IComponentFieldsGeneral }): JSX.Element => {
  const [page] = useQueryParams(QueryParamsConst.PAGE, '1');
  const [perPage] = useQueryParams(QueryParamsConst.PER_PAGE, '10');
  const queryKey = QueryParamsConst.QUERY;
  const pfeInputRef = useRef<any>(null);
  const formRef = useRef<any>(null);

  const [, setSuggestionQuery] = useState('');
  const [isSuggestionsComponentVisible, setIsSuggestionsComponentVisible] = useState(false);

  const SUGGESTIONS_MIN_CHAR_LIMIT = 3;

  let submitUrl: URL;
  try {
    const href = fields.search?.search?.value?.href;
    const origin = windowService()?.location?.origin;

    submitUrl = href.startsWith('/') ? new URL(href, origin) : new URL(href);
  } catch (e) {
    console.warn(e);
  }

  const onSubmit: (e: FormEvent<HTMLFormElement>) => void = (e) => {
    e.preventDefault();
    if (submitUrl && pfeInputRef.current && !submitUrl.searchParams.has(queryKey)) {
      submitUrl.searchParams.append(queryKey, pfeInputRef.current.value);
      submitUrl.searchParams.append(QueryParamsConst.PAGE, page);
      submitUrl.searchParams.append(QueryParamsConst.PER_PAGE, perPage);
      windowService().location.href = submitUrl.toString();
    }
  };

  const updateSuggestionsQuery = (event): void => {
    setSuggestionQuery(event?.target?.value);
  };

  const onKeyUp: (e) => void = (e) => {
    if (e?.key === 'Enter') {
      onSubmit(e);
      return;
    }

    updateSuggestionsQuery(e); //debounce(updateSuggestionsQuery, SUGGESTIONS_DEBOUNCE_TIME_IN_MS)
  };

  const onUpdateQueryFromSuggestions = (q: string): void => {
    pfeInputRef.current.value = q;
    setIsSuggestionsComponentVisible(false);
    formRef.current.dispatchEvent(new Event('submit', {cancelable: true, bubbles: true}));
  };

  const handleClickOutside = (event): void => {
    if (formRef?.current?.contains(event?.target)) {
      return;
    }

    setIsSuggestionsComponentVisible(false);
  };

  useEffect(() => {
    documentService().addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService().removeEventListener('mouseup', handleClickOutside);
    };
  }, []);

  return (
    <NavigationItemMenuStructure
      triggerContent={<MenuTriggerContentWithIcon iconField={fields?.search?.icon} textValue={fields.search?.link?.value?.text} />}
      actionTitle={fields.search?.link?.value?.text}
      fullWidth={true}
    >
      <div className={'navigation__menu-item__tray__container navigation__menu-item__tray__container--search'}>
        <form action={fields.search?.search?.value?.href} onSubmit={onSubmit} ref={formRef}>
          <div className="search-wrapper">
            <div className="search-wrapper__searchbox">
              <div className="search-wrapper__searchbox--input">
                <Icon iconName="action-search" extraClassName="search-wrapper__icon" size={32} />
                <SearchInput
                  name="Search"
                  placeholder={fields.search?.placeholder?.value}
                  type="text"
                  className="search-wrapper__input"
                  ref={pfeInputRef}
                  onKeyUp={onKeyUp}
                  onFocus={(): void => setIsSuggestionsComponentVisible(true)}
                  autoComplete="off"
                />
              </div>
              <div className="search-wrapper__searchbox--button">
                <CTAButton type={ctaEnums.primaryRed}>
                  <button type="submit" className={'search__button'} disabled={!pfeInputRef?.current?.value}>
                    {fields.search?.search?.value?.text}
                  </button>
                </CTAButton>
              </div>
              {isSuggestionsComponentVisible && (
                <Suggestions
                  language={sitecoreContext?.language || ''}
                  contextId={sitecoreContext?.itemId || ''}
                  suggestionQuery={pfeInputRef?.current?.value}
                  minimalLength={SUGGESTIONS_MIN_CHAR_LIMIT}
                  onUpdateQuery={onUpdateQueryFromSuggestions}
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </NavigationItemMenuStructure>
  );
};

const Search = ({ fields, sitecoreContext }: WithSitecoreContextProps & ComponentProps): JSX.Element => {
  return <SearchMenu fields={fields} sitecoreContext={sitecoreContext} />;
};

export default Search;
